import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { NgxGalleryImage, NgxGalleryOptions } from '@kolkov/ngx-gallery';
import { Subject } from 'rxjs/internal/Subject';
import { PropertyService } from 'src/app/_shared/service/property.service';
import { ImageGalleryState } from 'src/app/_shared/state/image-gallery.state';
import { PropertyState } from 'src/app/workspace/state/property.state';
import constants from 'src/constant.json';
import { environment } from 'src/environments/environment';
import { CmaState } from '../cma.state';

@Component({
  selector: 'app-view-cma-property-details',
  templateUrl: './view-cma-property-details.component.html',
  styleUrls: ['./view-cma-property-details.component.scss'],
})
export class ViewCmaPropertyDetailsComponent implements OnInit {
  @ViewChild('mapDialog', { static: false }) mapDialog: any;
  @Output() moveToDiffSection: EventEmitter<any> = new EventEmitter<any>();

  private onDestroyNotifier: Subject<any> = new Subject<any>();
  defaultProfileImage = constants.userAvatar;
  mainGalleryOptions: NgxGalleryOptions[] = [];
  mainGalleryImages: NgxGalleryImage[] = [];
  smallGalleryImages: NgxGalleryImage[] = [];
  lastImage: NgxGalleryImage = {};
  setBirdsEyeViewEvent: object;
  displayImages: string[] = []; // Images to Show
  remainingImagesCount: number = 0; // Extra Image Count
  isGalleryOpen: boolean = false;
  mainImage: string;
  images = [];
  propertyDetails: any;
  propertyDetailsResponse: any;
  isExpanded: boolean = false;
  isStreetView: boolean;
  showStreetView: boolean = false;
  streetView = true;
  setStreetViewEvent: object;
  defaultPlace: { lat: number; lng: number };
  selectedMapView: string = 'streetView';
  showBirdEye: boolean = true;

  constructor(
    public imageGalleryState: ImageGalleryState,
    private cmaState: CmaState,
    public propertyState: PropertyState,
    public dialog: MatDialog,
    private propertyService: PropertyService,
    public dialogRef: MatDialogRef<ViewCmaPropertyDetailsComponent>
  ) {}

  ngOnInit(): void {
    this.cmaState.cmaReports.pipe().subscribe((res: any) => {
      if (res) {
        this.propertyDetails = res;
        this.propertyDetailsResponse = res.compReportData?.valueReport?.subject;
        if (this.propertyDetailsResponse) {
          let coordinate = {
            lat: Number(this.propertyDetailsResponse.Latitude),
            lng: Number(this.propertyDetailsResponse.Longitude),
          };
          this.defaultPlace = coordinate ? coordinate : null;
        }
        if (this.propertyDetails.listingId && this.propertyDetails.numberOfImages) {
          this.showStreetView = false;
          this.mainImage =
            environment.listingImageBaseUrl +
            '?ListingId=' +
            (this.propertyDetails.listingId || 'null') +
            '&imageName=1';
        } else {
          this.showStreetView = true;
          this.setStreetViewEvent = this.defaultPlace;
        }
        if (this.propertyDetails.listingId && this.propertyDetails.numberOfImages) {
          for (let index = 2; index < this.propertyDetails.numberOfImages; index++) {
            this.images.push(
              environment.listingImageBaseUrl +
                '?ListingId=' +
                (this.propertyDetails.listingId || 'null') +
                '&imageName=' +
                index
            );
          }
        }
        this.manageImageDisplay();
      }
    });
  }

  ngAfterViewInit() {
    this.imageGalleryState.imgGalleryList$.subscribe((res) => {
      if (res && res.length) {
        this.isGalleryOpen = true;
      } else {
        this.isGalleryOpen = false;
      }
    });
  }

  openPropertyDetails(property) {
    property.PMXPropertyId = property.pmxPropertyId ? property.pmxPropertyId : '';
    property.ListingId = property.listingId ? property.listingId : '';
    this.propertyState.propertyDetailsSheetInput = property;
  }

  toggleShowMore() {
    this.isExpanded = !this.isExpanded;
  }

  moveToValuation() {
    this.moveToDiffSection.emit('valuation');
  }

  manageImageDisplay() {
    const totalImages = this.images.length;
    if (totalImages < 3 || totalImages === 3 || totalImages === 6) {
      this.displayImages = this.images; // Show all images
      this.remainingImagesCount = 0;
    } else if (totalImages > 3 && totalImages < 6) {
      this.displayImages = this.images.slice(0, 3); // Show first 3 images
      this.remainingImagesCount = totalImages - 3;
    } else if (totalImages > 6) {
      this.displayImages = this.images.slice(0, 6);
      this.remainingImagesCount = totalImages - 6;
    }
  }

  openGallery(index: number): void {
    this.imageGalleryState.imgGalleryCurrentIndex = index;
    this.imageGalleryState.imgGalleryListValue = this.images;
  }

  closeGallery() {
    this.imageGalleryState.imgGalleryListValue = null;
  }

  closeDialog() {
    this.dialogRef.close();
  }

  showMap() {
    if (this.selectedMapView === 'birdsEyeView') {
      this.setStreetViewEvent = null;
      this.setBirdsEyeViewEvent = this.defaultPlace;
      this.isStreetView = false;
    } else {
      this.setBirdsEyeViewEvent = null;
      this.setStreetViewEvent = this.defaultPlace;
      this.isStreetView = true;
    }
    this.dialogRef = this.dialog.open(this.mapDialog);
  }

  isReportType(): boolean {
    return ['PublicRecordCMA', 'PublicRecordSmartResearch', 'compCheck'].includes(this.propertyDetails?.reportType);
  }

  onStreetViewAvailable(isAvailable: boolean) {
    this.showBirdEye = isAvailable;
  }

  onViewChange(viewChange: MatRadioChange) {
    this.selectedMapView = viewChange.source.value;
    if (this.selectedMapView === 'birdsEyeView') {
      this.setStreetViewEvent = null;
      this.isStreetView = false;
      this.setBirdsEyeViewEvent = this.defaultPlace;
    } else {
      this.setStreetViewEvent = this.defaultPlace;
      this.isStreetView = true;
      this.setBirdsEyeViewEvent = null;
    }
  }

  getStatusClass(status: string): string {
    if (!status) return '';
    const normalized = status.trim().toLowerCase();
    if (['active', 'active under contract', 'pending', 'active uc'].includes(normalized)) {
      return 'listing';
    } else if (['auction', 'pre-foreclosure', 'reo / bank owned'].includes(normalized)) {
      return 'distressed';
    } else if (['fannie mae owned', 'hud owned'].includes(normalized)) {
      return 'hud';
    } else if (normalized === 'sold') {
      return 'sold';
    }

    return '';
  }

  formatYesNo(value: string | null | undefined): string {
    const v = value?.toUpperCase();
    if (v === 'Y') return 'Yes';
    if (v === 'N') return 'No';
    return '--';
  }

  ngOnDestroy() {
    this.onDestroyNotifier.next();
    this.onDestroyNotifier.complete();
  }
}
